/**
 * Module-level toast API. Any code (axios interceptors, hook callbacks,
 * service helpers) can call `toast.success("...")` without threading a
 * React context through.
 *
 * The Toaster component (`components/ui/Toaster.tsx`) registers its
 * dispatch function on mount via `registerToastHandler`. Calls before
 * the Toaster mounts are silently dropped (early-startup race window
 * is normally < 1 frame; not worth queueing).
 *
 * Design ported from the Kora admin template's ToastPage —
 * (template/Kora-Main-File/Template/src/pages/components/ToastPage.tsx)
 * — but adapted to our Tailwind theme tokens (mk-card, bg-card,
 * border-border, text-text-primary, accent etc) instead of
 * `bg-white dark:bg-gray-800` so the toasts match the rest of the
 * Kora-dark-theme app.
 */

import type { ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export type ToastPosition =
  | 'top-left'
  | 'top-right'
  | 'top-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center';

export interface ToastInput {
  message: ReactNode;
  heading?: ReactNode;
  variant?: ToastVariant;
  /** ms before auto-dismiss. 0 = permanent until user closes. Default 5000. */
  duration?: number;
  position?: ToastPosition;
  action?: ReactNode;
  onDismiss?: () => void;
}

type ToastHandler = (input: ToastInput) => string;

let _addToast: ToastHandler | null = null;

/** Called by <Toaster> on mount. Internal API. */
export function registerToastHandler(fn: ToastHandler | null): void {
  _addToast = fn;
}

function fire(variant: ToastVariant) {
  return (message: ReactNode, opts: Omit<ToastInput, 'message' | 'variant'> = {}) =>
    _addToast?.({ ...opts, variant, message });
}

export const toast = {
  success: fire('success'),
  error: fire('error'),
  warning: fire('warning'),
  info: fire('info'),
  show: (input: ToastInput) => _addToast?.(input),
};
